import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import {
  ESegmentedToggleComponent,
  ESegmentedToggleOption,
} from '@shared/components/atoms/inputs/e-segmented-toggle/e-segmented-toggle.component';
import { HdChartMatchScope } from '../bodygraph/chart-tabs/hd-chart-global-settings';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdChartSearchContextService } from './hd-chart-search-context.service';

/**
 * Snelkoppeling boven de resultaten van een zoektocht: zoeken in de hele
 * bibliotheek of enkel in charts met een relatie tot de bronchart. Wijzigt
 * dezelfde gebruikerssetting als het instellingenpaneel (via de chart-pagina),
 * zodat je dat ter plekke kunt omzetten. Enkel aangemaakt waar de pagina een
 * setter registreerde (zie `hd-chart-match-list`).
 */
@Component({
  selector: 'hd-chart-match-scope-toggle',
  standalone: true,
  imports: [ESegmentedToggleComponent],
  template: `
    <e-segmented-toggle
      size="xs"
      [options]="options()"
      [value]="context.matchScope()"
      [groupLabel]="groupLabel()"
      testId="chart-match-scope"
      (valueChange)="setScope($event)"
    ></e-segmented-toggle>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChartMatchScopeToggleComponent {
  protected readonly context = inject(HdChartSearchContextService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  private t(key: string): string {
    return this.transloco.translate<string>(key, undefined, this.lang());
  }

  protected readonly groupLabel = computed(() => this.t('humanDesign.chart.controls.rows.chartMatchScope'));

  protected readonly options = computed<readonly ESegmentedToggleOption<HdChartMatchScope>[]>(() => [
    {
      value: 'library',
      label: this.t('humanDesign.chartSearch.scopeLibrary'),
      title: this.t('humanDesign.chart.controls.tooltips.chartMatchScopeLibrary'),
    },
    {
      value: 'related',
      label: this.t('humanDesign.chartSearch.scopeRelated'),
      title: this.t('humanDesign.chart.controls.tooltips.chartMatchScopeRelated'),
    },
  ]);

  protected setScope(scope: HdChartMatchScope): void {
    this.context.matchScopeSetter()?.(scope);
  }
}
